import { forwardRef } from 'react';
import { cn } from '../lib/utils.js';
import { controlClass } from '../lib/control.js';

/* ===========================================================================
 * THE CONTROL THE READER SEES BEFORE ANYTHING OPENS.
 *
 * It is `controlClass` — the SAME constant `<Input>`, `<Select>` and
 * `<Combobox>` are skinned from — on a `<button>`. Not a copy of it: §6 of
 * docs/design-rules.md and the header of `lib/control.ts` both say the same
 * thing, that a fourth private spelling of "border, corners, focus, invalid,
 * disabled" is exactly the drift that constant exists to stop. A date field
 * standing in a form row beside a text field has to share its baseline, its
 * 40px height and its focus treatment, and the only way to guarantee that is to
 * share the string.
 *
 * WHY A BUTTON AND NOT AN INPUT. The native `<input type="date">` is what this
 * replaces, and its defect is that it renders itself: order, separators and
 * typeface all come from the reader's OPERATING SYSTEM, which is how the
 * reports workspace came to write `09 / 01 / 2026` beside a table writing
 * `01-09-2026`. A button draws its own text, so the app's day-first order holds
 * on every machine. Typing is not lost — it moves inside the popover, where a
 * real text field takes `05-09-2026` and says what is wrong with `31-09-2026`.
 * ======================================================================== */

export interface DateTriggerProps {
  /** The formatted value, or empty for the placeholder. */
  text: string;
  placeholder: string;
  open: boolean;
  panelId: string;
  onToggle: () => void;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  /**
   * Announced as `aria-required`, NOT rendered as a marker.
   *
   * The trigger is a `<button>`, so there is no native `required` to inherit —
   * the whole date family took no `required` prop at all, and the migration off
   * `<input type="date">` therefore carried the visible asterisk across and
   * silently dropped the machine-readable half: three genuinely required fields
   * (joining date, and the correction's date and time) announced as optional to
   * anyone not looking at the screen.
   */
  required?: boolean;
  glyph: 'calendar' | 'clock';
  className?: string;
}

export const DateTrigger = forwardRef<HTMLButtonElement, DateTriggerProps>(
  (
    {
      text,
      placeholder,
      open,
      panelId,
      onToggle,
      disabled,
      invalid,
      id,
      ariaLabel,
      ariaLabelledBy,
      ariaDescribedBy,
      required,
      glyph,
      className,
    },
    ref
  ) => (
    <button
      ref={ref}
      id={id}
      type="button"
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-required={required || undefined}
      aria-invalid={invalid || undefined}
      onClick={onToggle}
      className={cn(controlClass, 'items-center justify-between gap-element text-start', className)}
    >
      {/* The placeholder is INK, not a value: `text-muted-foreground` is the
          same level `controlClass` gives a real input's placeholder, so an
          empty date field and an empty text field read as equally empty. */}
      <span className={cn('truncate', text ? 'text-foreground' : 'text-muted-foreground')}>
        {text || placeholder}
      </span>
      <TrailingGlyph glyph={glyph} />
    </button>
  )
);
DateTrigger.displayName = 'DateTrigger';

/* Inline SVG rather than a lucide import, for one reason: these two glyphs are
   part of the CONTROL's anatomy — 16px on a 1.6 stroke, matching the icon
   scale's `ICON.control` step — and they must not change under a library
   upgrade the way a shared icon set can. Everything else in this package that
   draws a glyph inside a control does the same. */
function TrailingGlyph({ glyph }: { glyph: 'calendar' | 'clock' }): JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="flex-none text-foreground-tertiary"
    >
      {glyph === 'calendar' ? (
        <>
          <rect x="2" y="3.25" width="12" height="10.75" rx="2" />
          <path d="M2 6.5h12M5.25 1.75v3M10.75 1.75v3" />
        </>
      ) : (
        <>
          <circle cx="8" cy="8" r="6.25" />
          <path d="M8 4.5V8l2.25 1.5" />
        </>
      )}
    </svg>
  );
}
